import React from 'react';
import { Outlet } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Button,
  useTheme,
} from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { useAuth } from '../hooks';
import { useAppTheme } from '../contexts/ThemeContext';

export const MainLayout: React.FC = () => {
  const { user, isLoggedIn, logout } = useAuth();
  const theme = useTheme();
  const { toggleColorMode } = useAppTheme();
  const logoSrc = theme.palette.mode === 'dark'
    ? '/streamy-logo-navy-blue-background-white-text.png'
    : '/streamy-logo-white-background-navy-blue-text.png';

  // Determine the title based on the user's role and login state
  const getAppBarTitle = () => {
    if (!isLoggedIn) {
      return 'Streamy Platform'; // Generic title for logged-out users
    }
    switch (user?.role) {
      case 'BROADCASTER':
        return `${user.email}'s Dashboard`;
      default:
        return 'Dashboard';
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" color="primary">
        <Toolbar>
          <Box
            sx={{
              width: { xs: 96, sm: 126 },
              height: 36,
              mr: 2,
              px: 1,
              display: 'flex',
              alignItems: 'center',
              bgcolor: theme.palette.mode === 'dark' ? 'background.paper' : '#fff',
              borderRadius: 1,
              overflow: 'hidden',
              flexShrink: 0,
            }}
          >
            <Box
              component="img"
              src={logoSrc}
              alt="Streamy"
              sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          </Box>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {getAppBarTitle()}
          </Typography>
          
          <IconButton sx={{ ml: 1 }} onClick={toggleColorMode} color="inherit">
            {theme.palette.mode === 'dark' ? <Brightness7Icon /> : <Brightness4Icon />}
          </IconButton>
          
          {/* Conditionally render the Logout button only if the user is logged in */}
          {isLoggedIn && (
            <Button color="inherit" startIcon={<PowerSettingsNewIcon />} onClick={logout}>
              Logout
            </Button>
          )}
        </Toolbar>
      </AppBar>
      
      {/* The Outlet component from react-router-dom renders the matched child route's component */}
      <main>
        <Outlet />
      </main>
    </Box>
  );
};
